// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  AccountStatistics,
  BankAccount,
  TransactionPage,
  UserStatus,
} from '../types/openBanking';

/**
 * Advisor UI's wrapper around the {@code /api/openbanking/**} proxy on
 * advisor-bff. The {@code userId} arg is the **client's** federated
 * (Keycloak) user id — sourced from {@code ClientDetailDTO.federatedUserId}
 * in the calling tab — NOT the advisor's JWT subject.
 *
 * <p>Authorisation (V1): brokerage-scope only. open-banking-server
 * enforces that the calling JWT's {@code brokerage_id} claim matches the
 * brokerage_id stored on every row it returns. There is no per-client
 * ownership check — any advisor in the brokerage can read any client's
 * data. Tightening this is a documented follow-up.
 */

export interface TransactionsQuery {
  pageNumber?: number;
  pageSize?: number;
  fromDate?: string;
  toDate?: string;
}

const root = (userId: string) => `/openbanking/user/${encodeURIComponent(userId)}`;

const openBankingApi = {
  async getStatus(userId: string): Promise<UserStatus> {
    const { data } = await api.get<UserStatus>(`${root(userId)}/status`);
    return data;
  },

  async getAccounts(userId: string): Promise<BankAccount[]> {
    const { data } = await api.get<BankAccount[]>(`${root(userId)}/accounts`);
    return data;
  },

  async getTransactions(
    userId: string,
    accountId: string,
    query: TransactionsQuery = {},
  ): Promise<TransactionPage> {
    const { data } = await api.get<TransactionPage>(
      `${root(userId)}/account/${encodeURIComponent(accountId)}/transactions`,
      { params: stripUndefined({ ...query }) },
    );
    return data;
  },

  async getStatistics(userId: string, accountId: string): Promise<AccountStatistics> {
    const { data } = await api.get<AccountStatistics>(
      `${root(userId)}/account/${encodeURIComponent(accountId)}/statistics`,
    );
    return data;
  },
};

function stripUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') {
      out[k] = v;
    }
  }
  return out;
}

export default openBankingApi;
